/**
 * The per-row tick. One component for every segment so the hit target and the
 * label are identical whether the row is draggable, grouped or plain.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Stops pointer and click propagation: queued rows are drag handles and grouped
 * rows expand on click, so without this, selecting a row would start a drag or
 * toggle a section instead.
 */

import { FC } from 'react';
import { Check, Loader2 } from 'lucide-react';
import type { QueueElement } from '@trafficpulse/shared';

import { Checkbox } from '@/components/ui';

export type BulkRowStatus = 'idle' | 'running' | 'done';

export interface SelectCheckboxProps {
  element: QueueElement;
  checked: boolean;
  onToggle: (el: QueueElement) => void;
  disabled?: boolean;
  /** Where this row is in a running batch. Every segment shares this cell, so
   *  one indicator here covers all four views. */
  status?: BulkRowStatus;
}

const SelectCheckbox: FC<SelectCheckboxProps> = ({
  element,
  checked,
  onToggle,
  disabled,
  status = 'idle',
}) => (
  <span
    className="flex h-4 w-4 shrink-0 items-center justify-center"
    onPointerDown={(e) => e.stopPropagation()}
    onClick={(e) => e.stopPropagation()}
  >
    {status === 'running' ? (
      <Loader2
        className="h-3.5 w-3.5 animate-spin text-primary"
        aria-label={`Updating ${element.itemName || 'element'}`}
        data-testid={`busy-${element.bankId}`}
      />
    ) : status === 'done' ? (
      <Check
        className="h-3.5 w-3.5 text-primary"
        aria-label={`${element.itemName || 'Element'} updated`}
        data-testid={`done-${element.bankId}`}
      />
    ) : (
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={() => onToggle(element)}
        aria-label={`Select ${element.itemName || 'element'}`}
        data-testid={`select-${element.bankId}`}
      />
    )}
  </span>
);

export default SelectCheckbox;
